import ModalLayout from "@/components/layout/ModalLayout";
import { Button } from "@/components/ui/Button";
import { InputBox } from "@/components/ui/InputBox";
import DateOneBox from "@/components/ui/DateOneBox";
import SelectBox from "@/components/ui/SelectBox";
import ModalHeader from "@/components/ui/ModalHeader";
import { useState, useEffect } from "react";
import { ReportData, SaleSlip } from "@/interface";
import {
  useCodeListByCargoForm,
  useCodeListByCargoUnit,
  useCodeListByCompanyCode,
} from "@/hooks/standard/st_code/useCode";
import CompanySearchModal from "./CompanySearchModal";
import { StCompanyCode } from "@/interface/standard/st_company_code";
import ItemCodeSearchModal from "./ItemCodeSearchModal";
import { ItemCode } from "@/interface/standard/st_item_code";
import CarNumberSearchModal from "./CarNumberSearchModal";
import { VehicleCode } from "@/interface/standard/st_vehicle_code";
import DriverSearchModal from "./DriverSearchModal";
import { DriverCode } from "@/interface/standard/st_driver_code";
import { insertAndUpdateSaleSlip } from "@/hooks/performance/useSaleSlip";
import { useUserInfo } from "@/hooks/member/useMember";
import { getToday, getLocalISOString } from "@/lib/date";

interface PerformanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
  mode?: "add" | "edit" | "view";
  initialData?: ReportData;
}

export default function PerformanceModal({
  isOpen,
  onClose,
  onSave,
  mode = "add",
  initialData,
}: PerformanceModalProps) {
  console.log("initialData", initialData);
  const { data: cargoFormData } = useCodeListByCargoForm();
  const { data: cargoUnitData } = useCodeListByCargoUnit();
  const { data: companyCodeData } = useCodeListByCompanyCode();
  const { mutate: saveSaleSlip, isPending } = insertAndUpdateSaleSlip();
  const { data: userInfo } = useUserInfo();

  // 주요 정보
  const [companyCode, setCompanyCode] = useState(""); // 회사 코드
  const [companyGb, setCompanyGb] = useState("1"); // 회사 구분 (1: 계약+운송, 2: 계약+위탁)
  const [saleDate, setSaleDate] = useState(getToday());
  const [saleCustomer, setSaleCustomer] = useState(""); // 화면 표시용 (이름)
  const [saleCustomerCode, setSaleCustomerCode] = useState(""); // DB 저장용 (코드)
  const [itemName, setItemName] = useState(""); // 화면 표시용 (이름)
  const [itemCode, setItemCode] = useState(""); // DB 저장용 (코드)
  const [vehicleNumber, setVehicleNumber] = useState(""); // 화면 표시용 (차량번호)
  const [vehicleCode, setVehicleCode] = useState(""); // DB 저장용 (코드)
  const [driverName, setDriverName] = useState(""); // 화면 표시용 (이름)
  const [driverCode, setDriverCode] = useState(""); // DB 저장용 (코드)
  const [phoneNumber, setPhoneNumber] = useState("");
  const [cargoType, setCargoType] = useState("");
  const [cargoUnit, setCargoUnit] = useState("");

  // 상차지 정보
  const [loadingAgency, setLoadingAgency] = useState("");
  const [loadingLocation, setLoadingLocation] = useState("");
  const [loadingQty, setLoadingQty] = useState("");
  const [departure, setDeparture] = useState("");

  // 하차지 정보
  const [customer, setCustomer] = useState("");
  const [area1, setArea1] = useState("");
  const [area2, setArea2] = useState("");
  const [area3, setArea3] = useState("");
  const [arrival, setArrival] = useState("");
  const [qty1, setQty1] = useState("");
  const [qty2, setQty2] = useState("");
  const [qty3, setQty3] = useState("");

  // 매출정보
  const [saleUnitPrice, setSaleUnitPrice] = useState("");
  const [saleAmount, setSaleAmount] = useState("");
  const [saleMoveFee, setSaleMoveFee] = useState("");
  const [saleVat, setSaleVat] = useState("");
  const [saleTotal, setSaleTotal] = useState("");

  // 매입정보
  const [buyUnitPrice, setBuyUnitPrice] = useState("");
  const [buyAmount, setBuyAmount] = useState("");
  const [buyMoveFee, setBuyMoveFee] = useState("");
  const [buyLaborFee, setBuyLaborFee] = useState("");
  const [buyMultiFee, setBuyMultiFee] = useState("");
  const [buyWeightFee, setBuyWeightFee] = useState("");
  const [buyVat, setBuyVat] = useState("");
  const [buyTotal, setBuyTotal] = useState("");

  // 운송마진
  const [transportProfitAmt, setTransportProfitAmt] = useState("");
  const [transportProfitRate, setTransportProfitRate] = useState("");

  // 기타
  const [emptyReturn, setEmptyReturn] = useState("Y"); // 공차회차
  const [remark, setRemark] = useState("");

  // 등록/수정 정보
  const [createdBy, setCreatedBy] = useState("");
  const [createdDate, setCreatedDate] = useState("");
  const [updatedBy, setUpdatedBy] = useState("");
  const [updatedDate, setUpdatedDate] = useState("");
  const [code, setCode] = useState("");

  // 모달 상태
  const [isCompanySearchOpen, setIsCompanySearchOpen] = useState(false);
  const [isItemSearchOpen, setIsItemSearchOpen] = useState(false);
  const [isCarNumberSearchOpen, setIsCarNumberSearchOpen] = useState(false);
  const [isDriverSearchOpen, setIsDriverSearchOpen] = useState(false);

  // initialData가 변경될 때 폼 필드 초기화
  useEffect(() => {
    if (initialData && (mode === "edit" || mode === "add" || mode === "view")) {
      // 주요 정보
      if (initialData.saleSlipCompanyCode !== undefined && initialData.saleSlipCompanyCode !== null)
        setCompanyCode(initialData.saleSlipCompanyCode);
      if (initialData.saleSlipCompanyGb !== undefined && initialData.saleSlipCompanyGb !== null)
        setCompanyGb(initialData.saleSlipCompanyGb);
      if (initialData.custName) {
        setSaleCustomer(initialData.custName);
      }
      if (initialData.saleSlipCustCode !== undefined && initialData.saleSlipCustCode !== null)
        setSaleCustomerCode(initialData.saleSlipCustCode);
      if (initialData.transportDoneDate) {
        const dateStr = initialData.transportDoneDate.replace("T", " ").split(" ")[0];
        setSaleDate(dateStr);
      }
      if (initialData.vehicleNumber) {
        setVehicleNumber(initialData.vehicleNumber);
      }
      if (initialData.saleSlipCarCode !== undefined && initialData.saleSlipCarCode !== null)
        setVehicleCode(initialData.saleSlipCarCode);
      if (initialData.saleSlipShapeCode !== undefined && initialData.saleSlipShapeCode !== null)
        setCargoType(initialData.saleSlipShapeCode);
      if (initialData.itemName !== undefined && initialData.itemName !== null) {
        setItemName(initialData.itemName);
      }
      if (initialData.saleSlipItemCode !== undefined && initialData.saleSlipItemCode !== null)
        setItemCode(initialData.saleSlipItemCode);
      if (initialData.driverName !== undefined && initialData.driverName !== null) {
        setDriverName(initialData.driverName);
      }
      if (initialData.saleSlipDriverCode !== undefined && initialData.saleSlipDriverCode !== null)
        setDriverCode(initialData.saleSlipDriverCode);
      if (initialData.driverTel !== undefined && initialData.driverTel !== null) setPhoneNumber(initialData.driverTel);
      if (initialData.saleSlipUnitCode !== undefined && initialData.saleSlipUnitCode !== null)
        setCargoUnit(initialData.saleSlipUnitCode);

      // 상차지 정보
      if (initialData.pickupBranch) {
        setLoadingAgency(initialData.pickupBranch); // 대리점
      }
      if (initialData.pickupLocation) {
        setLoadingLocation(initialData.pickupLocation);
        setDeparture(initialData.pickupLocation);
      }
      if (initialData.pickupQty !== undefined)
        setLoadingQty(initialData.pickupQty !== 0 ? String(initialData.pickupQty) : "0");

      // 하차지 정보
      if (initialData.dropoffLocation) {
        setCustomer(initialData.dropoffLocation);
        setArrival(initialData.dropoffLocation);
      }
      if (initialData.dropoffArea1) setArea1(initialData.dropoffArea1);
      if (initialData.dropoffArea2) setArea2(initialData.dropoffArea2);
      if (initialData.dropoffArea3) setArea3(initialData.dropoffArea3);
      if (initialData.dropoffQty1 !== undefined)
        setQty1(initialData.dropoffQty1 !== 0 ? String(initialData.dropoffQty1) : "0");
      if (initialData.dropoffQty2 !== undefined)
        setQty2(initialData.dropoffQty2 !== 0 ? String(initialData.dropoffQty2) : "0");
      if (initialData.dropoffQty3 !== undefined)
        setQty3(initialData.dropoffQty3 !== 0 ? String(initialData.dropoffQty3) : "0");

      // 매출정보
      if (initialData.saleUnitPrice !== undefined) setSaleUnitPrice(initialData.saleUnitPrice.toLocaleString());
      if (initialData.saleAmount !== undefined) setSaleAmount(initialData.saleAmount.toLocaleString());
      if (initialData.saleMoveFee !== undefined)
        setSaleMoveFee(initialData.saleMoveFee !== 0 ? initialData.saleMoveFee.toLocaleString() : "0");
      if (initialData.saleVat !== undefined) setSaleVat(initialData.saleVat.toLocaleString());
      if (initialData.saleTotal !== undefined) setSaleTotal(initialData.saleTotal.toLocaleString());

      // 계약금액
      // contractAmount는 모달에 직접 필드가 없지만 필요시 사용 가능

      // 매입정보
      if (initialData.buyUnitPrice !== undefined) setBuyUnitPrice(initialData.buyUnitPrice.toLocaleString());
      if (initialData.buyAmount !== undefined) setBuyAmount(initialData.buyAmount.toLocaleString());
      if (initialData.buyMoveFee !== undefined)
        setBuyMoveFee(initialData.buyMoveFee !== 0 ? initialData.buyMoveFee.toLocaleString() : "0");
      if (initialData.buyLaborFee !== undefined)
        setBuyLaborFee(initialData.buyLaborFee !== 0 ? initialData.buyLaborFee.toLocaleString() : "0");
      if (initialData.buyMultiFee !== undefined)
        setBuyMultiFee(initialData.buyMultiFee !== 0 ? initialData.buyMultiFee.toLocaleString() : "0");
      if (initialData.buyWeightFee !== undefined)
        setBuyWeightFee(initialData.buyWeightFee !== 0 ? initialData.buyWeightFee.toLocaleString() : "0");
      if (initialData.buyVat !== undefined) setBuyVat(initialData.buyVat.toLocaleString());
      // buyTotal은 자동 계산되므로 초기값 설정 불필요

      // 운송마진
      if (initialData.transportProfitAmt !== undefined)
        setTransportProfitAmt(initialData.transportProfitAmt.toLocaleString());
      if (initialData.transportProfitRate !== undefined)
        setTransportProfitRate(String(initialData.transportProfitRate));

      // 코드 - saleSlipCodeForTieBreak 또는 contractUniqueNo 사용
      if (initialData.saleSlipCodeForTieBreak) {
        setCode(initialData.saleSlipCodeForTieBreak);
      } else if (initialData.contractUniqueNo) {
        setCode(initialData.contractUniqueNo);
      }

      // 등록/수정 정보 (edit 모드에서는 최초등록은 기존 값, 최종수정은 현재 사용자)
      if (initialData.saleSlipWriteId1) setCreatedBy(initialData.saleSlipWriteId1);
      if (initialData.saleSlipWriteDate1) setCreatedDate(initialData.saleSlipWriteDate1);
      // edit 모드일 때 최종수정은 저장 시점에 현재 사용자로 설정됨
      if (initialData.saleSlipWriteId2) setUpdatedBy(initialData.saleSlipWriteId2);
      if (initialData.saleSlipWriteDate2) setUpdatedDate(initialData.saleSlipWriteDate2);

      // 공차회차
      if (initialData.saleSlipReturnGb !== undefined) {
        setEmptyReturn(initialData.saleSlipReturnGb ? "Y" : "N");
      }

      // 비고
      if (initialData.saleSlipRemark !== undefined && initialData.saleSlipRemark !== null)
        setRemark(initialData.saleSlipRemark);
    }

    // Add 모드이고 initialData가 없을 때만 기본값으로 초기화
    if (mode === "add" && !initialData) {
      // 추가 모드일 때 기본값으로 초기화
      setCompanyCode("");
      setCompanyGb("1");
      setSaleDate(getToday());
      setSaleCustomer("");
      setSaleCustomerCode("");
      setItemName("");
      setItemCode("");
      setVehicleNumber("");
      setVehicleCode("");
      setDriverName("");
      setDriverCode("");
      setPhoneNumber("");
      setCargoType("");
      setCargoUnit("");
      setLoadingAgency("");
      setLoadingLocation("");
      setLoadingQty("");
      setDeparture("");
      setCustomer("");
      setArea1("");
      setArea2("");
      setArea3("");
      setArrival("");
      setQty1("");
      setQty2("");
      setQty3("");
      setSaleUnitPrice("");
      setSaleAmount("");
      setSaleMoveFee("");
      setSaleVat("");
      setSaleTotal("");
      setBuyUnitPrice("");
      setBuyAmount("");
      setBuyMoveFee("");
      setBuyLaborFee("");
      setBuyMultiFee("");
      setBuyWeightFee("");
      setBuyVat("");
      setBuyTotal("");
      setTransportProfitAmt("");
      setTransportProfitRate("");
      setCode("");
      setEmptyReturn("Y");
      setRemark("");
      // add 모드일 때 현재 로그인한 사용자로 등록/수정 정보 설정
      const currentUser = userInfo?.name || "";
      setCreatedBy(currentUser);
      setCreatedDate("");
      setUpdatedBy(currentUser);
      setUpdatedDate("");
    }
  }, [initialData, mode, isOpen, userInfo]);

  // 매출정보 - 합계 및 부가세 자동 계산
  // 매출액이 직접 입력된 경우 (주문일보 단가 → 매출액) 해당 값을 사용
  useEffect(() => {
    const amount = parseFloat(saleAmount.replace(/,/g, "")) || 0;
    const moveFee = parseFloat(saleMoveFee.replace(/,/g, "")) || 0;

    // 합계 = 매출액 + 직배비
    const total = amount + moveFee;
    setSaleTotal(total > 0 ? total.toLocaleString() : "0");

    // 부가세액 = (매출액 + 직배비) × 0.1 (소수점 이하 절사)
    const vat = Math.floor(total * 0.1);
    setSaleVat(vat > 0 ? vat.toLocaleString() : "0");
  }, [saleAmount, saleMoveFee]);

  // 매입정보 - 합계 자동 계산 (매입액 + 다착비 + 계근비)
  useEffect(() => {
    const amount = parseFloat(buyAmount.replace(/,/g, "")) || 0;
    const multiFee = parseFloat(buyMultiFee.replace(/,/g, "")) || 0;
    const weightFee = parseFloat(buyWeightFee.replace(/,/g, "")) || 0;

    // 합계 = 매입액 + 다착비 + 계근비
    const total = amount + multiFee + weightFee;
    setBuyTotal(total > 0 ? total.toLocaleString() : "0");

    // 부가세액 = 합계 × 0.1 (소수점 이하 절사)
    const vat = Math.floor(total * 0.1);
    setBuyVat(vat > 0 ? vat.toLocaleString() : "0");
  }, [buyAmount, buyMultiFee, buyWeightFee]);

  // 초기 로드 시 운송마진 비율로 매입정보 자동 계산
  // initialData에서 transportProfitRate가 전달되고 buyAmount가 0인 경우에만 계산
  useEffect(() => {
    if (!initialData) return;

    // 매출액(saleAmount) 기준으로 계산 (부가세 포함 합계가 아님)
    const saleAmountNum = parseFloat(saleAmount.replace(/,/g, "")) || 0;
    const rate = parseFloat(transportProfitRate) || 0;
    const currentBuyAmount = parseFloat(buyAmount.replace(/,/g, "")) || 0;

    // 매출액과 비율이 있고, 매입액이 없을 때만 자동 계산
    if (saleAmountNum > 0 && rate > 0 && currentBuyAmount === 0) {
      // 매입액 = 매출액 × (100% - 비율%)
      // 예: 비율 10% → 매입액 = 매출액 × 90%
      const buyAmountCalc = Math.round(saleAmountNum * ((100 - rate) / 100));
      setBuyAmount(buyAmountCalc > 0 ? buyAmountCalc.toLocaleString() : "0");

      // 운송마진 금액 = 매출액 - 매입액
      const profitAmount = saleAmountNum - buyAmountCalc;
      setTransportProfitAmt(profitAmount > 0 ? profitAmount.toLocaleString() : "0");
    }
  }, [saleAmount, transportProfitRate, initialData]);

  // 운송마진 비율 변경 시 매입정보 자동 계산
  // 비율을 입력하면 매출액에서 역산하여 매입 금액 계산
  const handleTransportProfitRateChange = (value: string) => {
    setTransportProfitRate(value);

    // 매출액(saleAmount) 기준으로 계산 (부가세 포함 합계가 아님)
    const saleAmountNum = parseFloat(saleAmount.replace(/,/g, "")) || 0;
    const rate = parseFloat(value) || 0;

    if (saleAmountNum > 0 && rate > 0) {
      // 매입액 = 매출액 × (100% - 비율%)
      // 예: 비율 10% → 매입액 = 매출액 × 90%
      const buyAmountCalc = Math.round(saleAmountNum * ((100 - rate) / 100));
      setBuyAmount(buyAmountCalc > 0 ? buyAmountCalc.toLocaleString() : "0");

      // 운송마진 금액 = 매출액 - 매입액
      const profitAmount = saleAmountNum - buyAmountCalc;
      setTransportProfitAmt(profitAmount > 0 ? profitAmount.toLocaleString() : "0");
    }
  };

  // 운송마진 금액 자동 계산 (매출 합계 - 매입 합계)
  useEffect(() => {
    const saleSum = parseFloat(saleTotal.replace(/,/g, "")) || 0;
    const buySum = parseFloat(buyTotal.replace(/,/g, "")) || 0;

    // 금액 = 매출 합계 - 매입 합계
    const profitAmount = saleSum - buySum;
    setTransportProfitAmt(profitAmount !== 0 ? Math.round(profitAmount).toLocaleString() : "0");
  }, [saleTotal, buyTotal]);

  // 날짜 포맷팅 함수
  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr.replace(" ", "T"));
    if (isNaN(date.getTime())) return "-";
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  };

  // 작성자 정보 컴포넌트 (edit, view 모드에서 표시)
  const writeInfoComponent =
    (mode === "edit" || mode === "view") && initialData ? (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <span>
          최초: {initialData.saleSlipWriteId1 || "-"} ({formatDateTime(initialData.saleSlipWriteDate1)})
        </span>
        <span className="text-gray-300">|</span>
        <span>
          최종: {initialData.saleSlipWriteId2 || "-"} ({formatDateTime(initialData.saleSlipWriteDate2)})
        </span>
      </div>
    ) : null;

  const handleSave = () => {
    const currentUserId = userInfo?.name || "";
    const currentDateTime = getLocalISOString();

    console.log("💾 저장 전 State 확인:", {
      mode,
      saleCustomerCode,
      itemCode,
      vehicleCode,
      driverCode,
      companyCode,
      companyGb,
      createdBy,
      createdDate,
    });

    const saleSlipData: SaleSlip = {
      saleSlipCode: mode === "edit" && initialData?.saleSlipCodeForTieBreak ? initialData.saleSlipCodeForTieBreak : "",
      saleSlipDate: saleDate,
      saleSlipInBranch: loadingAgency,
      saleSlipInLocation: loadingLocation,
      saleSlipInQty: parseFloat(loadingQty.replace(/,/g, "")) || 0,
      saleSlipOutLocation: arrival,
      saleSlipOutArea1: area1,
      saleSlipOutQty1: parseFloat(qty1.replace(/,/g, "")) || 0,
      saleSlipOutArea2: area2,
      saleSlipOutQty2: parseFloat(qty2.replace(/,/g, "")) || 0,
      saleSlipOutArea3: area3 || null,
      saleSlipOutQty3: parseFloat(qty3.replace(/,/g, "")) || 0,
      saleSlipSalePrice: parseFloat(saleUnitPrice.replace(/,/g, "")) || 0,
      saleSlipSaleSupportAmt: parseFloat(saleAmount.replace(/,/g, "")) || 0,
      saleSlipSaleSurtaxAmt: parseFloat(saleVat.replace(/,/g, "")) || 0,
      saleSlipSaleMoveAmt: parseFloat(saleMoveFee.replace(/,/g, "")) || 0,
      saleSlipSaleTotalAmt: parseFloat(saleTotal.replace(/,/g, "")) || 0,
      saleSlipBuyPrice: parseFloat(buyUnitPrice.replace(/,/g, "")) || 0,
      saleSlipBuySupportAmt: parseFloat(buyAmount.replace(/,/g, "")) || 0,
      saleSlipBuySurtaxAmt: parseFloat(buyVat.replace(/,/g, "")) || 0,
      saleSlipBuyMoveAmt: parseFloat(buyMoveFee.replace(/,/g, "")) || 0,
      saleSlipBuyLaborAmt: parseFloat(buyLaborFee.replace(/,/g, "")) || 0,
      saleSlipBuyMultyAmt: parseFloat(buyMultiFee.replace(/,/g, "")) || 0,
      saleSlipBuyWeightAmt: parseFloat(buyWeightFee.replace(/,/g, "")) || 0,
      saleSlipBuyTotalAmt: parseFloat(buyTotal.replace(/,/g, "")) || 0,
      saleSlipProfitAmt: parseFloat(transportProfitAmt.replace(/,/g, "")) || 0,
      saleSlipProfitRate: parseFloat(transportProfitRate) || 0,
      saleSlipReturnGb: emptyReturn === "Y",
      saleSlipRemark: remark,
      saleSlipCustCode: saleCustomerCode && saleCustomerCode.trim() !== "" ? saleCustomerCode : null, // 코드 저장
      saleSlipItemCode: itemCode && itemCode.trim() !== "" ? itemCode : null, // 코드 저장
      saleSlipShapeCode: cargoType && cargoType.trim() !== "" ? cargoType : null,
      saleSlipUnitCode: cargoUnit && cargoUnit.trim() !== "" ? cargoUnit : null,
      saleSlipCarCode: vehicleCode && vehicleCode.trim() !== "" ? vehicleCode : null, // 코드 저장
      saleSlipDriverCode: driverCode && driverCode.trim() !== "" ? driverCode : null, // 코드 저장
      saleSlipAreaCode1: area1 && area1.trim() !== "" ? area1 : null,
      saleSlipAreaCode2: area2 && area2.trim() !== "" ? area2 : null,
      saleSlipOrderSlipCode:
        initialData?.saleSlipOrderSlipCode && initialData.saleSlipOrderSlipCode.trim() !== ""
          ? initialData.saleSlipOrderSlipCode
          : null,
      saleSlipCompanyCode: companyCode && companyCode.trim() !== "" ? companyCode : null, // 회사 코드
      saleSlipCompanyGb: companyGb, // 회사 구분 (1: 계약+운송, 2: 계약+위탁)
      // add 모드: 최초등록/수정 모두 현재 사용자
      // edit 모드: 최종수정만 현재 사용자, 최초등록은 기존 값 유지
      saleSlipWriteId1: mode === "add" ? currentUserId : createdBy || currentUserId,
      saleSlipWriteDate1: mode === "add" ? currentDateTime : createdDate || currentDateTime,
      saleSlipWriteId2: currentUserId,
      saleSlipWriteDate2: currentDateTime,
      saleSlipFileName: null,
      saleSlipLock: false, // 기본값 false
    };

    console.log("📤 백엔드로 전송할 데이터:", saleSlipData);

    saveSaleSlip(saleSlipData, {
      onSuccess: response => {
        console.log("✅ 저장 성공 응답:", response);
        onSave?.(); // onSave 콜백 호출 (있으면)
        onClose();
      },
      onError: (error: any) => {
        console.error("❌ 저장 실패:", error);
      },
    });
  };

  const handleContinuousSave = () => {
    const currentUserId = userInfo?.name || "";
    const currentDateTime = getLocalISOString();

    const saleSlipData: SaleSlip = {
      saleSlipCode: "", // 연속저장은 항상 새로운 데이터
      saleSlipDate: saleDate,
      saleSlipInBranch: loadingAgency,
      saleSlipInLocation: loadingLocation,
      saleSlipInQty: parseFloat(loadingQty.replace(/,/g, "")) || 0,
      saleSlipOutLocation: arrival,
      saleSlipOutArea1: area1,
      saleSlipOutQty1: parseFloat(qty1.replace(/,/g, "")) || 0,
      saleSlipOutArea2: area2,
      saleSlipOutQty2: parseFloat(qty2.replace(/,/g, "")) || 0,
      saleSlipOutArea3: area3 || null,
      saleSlipOutQty3: parseFloat(qty3.replace(/,/g, "")) || 0,
      saleSlipSalePrice: parseFloat(saleUnitPrice.replace(/,/g, "")) || 0,
      saleSlipSaleSupportAmt: parseFloat(saleAmount.replace(/,/g, "")) || 0,
      saleSlipSaleSurtaxAmt: parseFloat(saleVat.replace(/,/g, "")) || 0,
      saleSlipSaleMoveAmt: parseFloat(saleMoveFee.replace(/,/g, "")) || 0,
      saleSlipSaleTotalAmt: parseFloat(saleTotal.replace(/,/g, "")) || 0,
      saleSlipBuyPrice: parseFloat(buyUnitPrice.replace(/,/g, "")) || 0,
      saleSlipBuySupportAmt: parseFloat(buyAmount.replace(/,/g, "")) || 0,
      saleSlipBuySurtaxAmt: parseFloat(buyVat.replace(/,/g, "")) || 0,
      saleSlipBuyMoveAmt: parseFloat(buyMoveFee.replace(/,/g, "")) || 0,
      saleSlipBuyLaborAmt: parseFloat(buyLaborFee.replace(/,/g, "")) || 0,
      saleSlipBuyMultyAmt: parseFloat(buyMultiFee.replace(/,/g, "")) || 0,
      saleSlipBuyWeightAmt: parseFloat(buyWeightFee.replace(/,/g, "")) || 0,
      saleSlipBuyTotalAmt: parseFloat(buyTotal.replace(/,/g, "")) || 0,
      saleSlipProfitAmt: parseFloat(transportProfitAmt.replace(/,/g, "")) || 0,
      saleSlipProfitRate: parseFloat(transportProfitRate) || 0,
      saleSlipReturnGb: emptyReturn === "Y",
      saleSlipRemark: remark,
      saleSlipCustCode: saleCustomerCode && saleCustomerCode.trim() !== "" ? saleCustomerCode : null, // 코드 저장
      saleSlipItemCode: itemCode && itemCode.trim() !== "" ? itemCode : null, // 코드 저장
      saleSlipShapeCode: cargoType && cargoType.trim() !== "" ? cargoType : null,
      saleSlipUnitCode: cargoUnit && cargoUnit.trim() !== "" ? cargoUnit : null,
      saleSlipCarCode: vehicleCode && vehicleCode.trim() !== "" ? vehicleCode : null, // 코드 저장
      saleSlipDriverCode: driverCode && driverCode.trim() !== "" ? driverCode : null, // 코드 저장
      saleSlipAreaCode1: area1 && area1.trim() !== "" ? area1 : null,
      saleSlipAreaCode2: area2 && area2.trim() !== "" ? area2 : null,
      saleSlipOrderSlipCode:
        initialData?.saleSlipOrderSlipCode && initialData.saleSlipOrderSlipCode.trim() !== ""
          ? initialData.saleSlipOrderSlipCode
          : null,
      saleSlipCompanyCode: companyCode && companyCode.trim() !== "" ? companyCode : null, // 회사 코드
      saleSlipCompanyGb: companyGb, // 회사 구분 (1: 계약+운송, 2: 계약+위탁)
      // 연속저장은 항상 새 데이터이므로 모두 현재 사용자
      saleSlipWriteId1: currentUserId,
      saleSlipWriteDate1: currentDateTime,
      saleSlipWriteId2: currentUserId,
      saleSlipWriteDate2: currentDateTime,
      saleSlipFileName: null,
      saleSlipLock: false, // 기본값 false
    };

    console.log("📤 백엔드로 전송할 데이터:", saleSlipData);
    saveSaleSlip(saleSlipData, {
      onSuccess: () => {
        // 연속 저장이므로 모달을 닫지 않고 모든 값 유지
        // 사용자가 계속해서 동일한 조건으로 여러 건을 입력할 수 있도록 함
      },
    });
  };

  // 숫자 입력 시 천 단위 콤마 포맷팅
  const formatNumber = (value: string) => {
    // 빈 문자열이면 그대로 반환
    if (!value) return "";

    // 숫자와 소수점만 남기기
    const numericValue = value.replace(/[^\d.]/g, "");

    // 빈 문자열이면 그대로 반환
    if (!numericValue) return "";

    // 소수점이 여러 개 있으면 첫 번째 것만 유지
    const parts = numericValue.split(".");
    if (parts.length > 2) {
      parts[1] = parts.slice(1).join("");
      parts.length = 2;
    }

    // 정수 부분에 콤마 추가
    if (parts[0]) {
      const integerPart = parts[0].replace(/^0+/, "") || "0";
      parts[0] = Number(integerPart).toLocaleString();
    }

    return parts.join(".");
  };

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      title="실적신고 등록/수정"
      maxWidth="max-w-7xl"
      maxHeight="max-h-[95vh]"
      zIndexClass="z-[9999]"
      usePortal
      headerRight={writeInfoComponent}
    >
      <div className="flex flex-col gap-4">
        {/* 상단 헤더 영역 */}
        <div className="flex items-center justify-between border-b border-gray-300 pb-2">
          <div className="flex items-center gap-2">
            <span className="text-red-500 text-sm">*</span>
            <span className="text-sm text-gray-600">코드</span>
            <InputBox value={code} onChange={e => setCode(e.target.value)} className="w-40" readOnly />
            <SelectBox
              value={companyGb}
              onChange={setCompanyGb}
              options={[
                { value: "1", label: "계약 + 운송" },
                { value: "2", label: "계약 + 위탁" },
              ]}
              className="w-[180px]"
            />
          </div>
        </div>

        {/* 주요 정보 입력 영역 - 2열 구성 */}
        <div className="grid grid-cols-2 gap-4">
          {/* 좌측 열 */}
          <div className="space-y-3">
            <SelectBox
              label="회사명"
              value={companyCode}
              onChange={setCompanyCode}
              options={
                companyCodeData?.map(item => ({
                  value: item.code,
                  label: item.name,
                })) || []
              }
            />
            <DateOneBox label="매출일자" date={saleDate} onDateChange={setSaleDate} />
            <InputBox
              label="차량번호"
              value={vehicleNumber}
              onChange={e => setVehicleNumber(e.target.value)}
              showIcon
              onIconClick={() => setIsCarNumberSearchOpen(true)}
              readOnly
            />
            <SelectBox
              label="화물형태"
              value={cargoType}
              onChange={setCargoType}
              options={
                cargoFormData?.map(item => ({
                  value: item.code,
                  label: item.name,
                })) || []
              }
            />
          </div>

          {/* 우측 열 */}
          <div className="space-y-3">
            <InputBox
              label="매출처"
              value={saleCustomer}
              onChange={e => setSaleCustomer(e.target.value)}
              showIcon
              onIconClick={() => setIsCompanySearchOpen(true)}
              readOnly
            />
            <InputBox
              label="품목명"
              value={itemName}
              onChange={e => setItemName(e.target.value)}
              showIcon
              onIconClick={() => setIsItemSearchOpen(true)}
              readOnly
            />
            <InputBox
              label="기사명"
              value={driverName}
              onChange={e => setDriverName(e.target.value)}
              showIcon
              onIconClick={() => setIsDriverSearchOpen(true)}
              readOnly
            />
            <InputBox label="전화번호" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} />
            <SelectBox
              label="화물단위"
              value={cargoUnit}
              onChange={setCargoUnit}
              options={
                cargoUnitData?.map(item => ({
                  value: item.code,
                  label: item.name,
                })) || []
              }
            />
          </div>
        </div>

        {/* 상차지/하차지 정보 영역 */}
        <div className="flex flex-col gap-4">
          {/* 상차지 */}
          <div className="border border-gray-300 rounded p-4">
            <ModalHeader title="상차지" />
            <div className="grid grid-cols-4 gap-3 mt-3">
              <InputBox label="대리점" value={loadingAgency} onChange={e => setLoadingAgency(e.target.value)} />
              <InputBox label="상차지" value={loadingLocation} onChange={e => setLoadingLocation(e.target.value)} />
              <InputBox label="수량" value={loadingQty} onChange={e => setLoadingQty(e.target.value)} />
              <InputBox
                label="출발지"
                value={departure}
                onChange={e => setDeparture(e.target.value)}
              />
            </div>
          </div>

          {/* 하차지 */}
          <div className="border border-gray-300 rounded p-4">
            <ModalHeader title="하차지" />
            <div className="flex flex-col gap-3 mt-3">
              {/* 첫 줄: 거래처, 도착지 */}
              <div className="grid grid-cols-2 gap-3">
                <InputBox label="거래처" value={customer} onChange={e => setCustomer(e.target.value)} />
                <InputBox
                  label="도착지"
                  value={arrival}
                  onChange={e => setArrival(e.target.value)}
                />
              </div>
              {/* 두 번째 줄: 지역[1], 수량[1] */}
              <div className="grid grid-cols-2 gap-3">
                <InputBox label="지역[1]" value={area1} onChange={e => setArea1(e.target.value)} />
                <InputBox label="수량[1]" value={qty1} onChange={e => setQty1(e.target.value)} />
              </div>
              {/* 세 번째 줄: 지역[2], 수량[2] */}
              <div className="grid grid-cols-2 gap-3">
                <InputBox label="지역[2]" value={area2} onChange={e => setArea2(e.target.value)} />
                <InputBox label="수량[2]" value={qty2} onChange={e => setQty2(e.target.value)} />
              </div>
              {/* 네 번째 줄: 지역[3], 수량[3] */}
              <div className="grid grid-cols-2 gap-3">
                <InputBox label="지역[3]" value={area3} onChange={e => setArea3(e.target.value)} />
                <InputBox label="수량[3]" value={qty3} onChange={e => setQty3(e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        {/* 재무 및 기타 정보 영역 */}
        <div className="flex flex-col gap-4">
          {/* 매출정보 */}
          <div className="border-2 border-red-500 rounded p-3">
            <ModalHeader title="매출정보" />
            <div className="grid grid-cols-3 gap-3 mt-3">
              <InputBox
                label="단가"
                value={saleUnitPrice}
                onChange={e => setSaleUnitPrice(formatNumber(e.target.value))}
              />
              <InputBox label="매출액" value={saleAmount} onChange={e => setSaleAmount(formatNumber(e.target.value))} />
              <InputBox label="부가세액" value={saleVat} onChange={e => setSaleVat(formatNumber(e.target.value))} />
              <InputBox
                label="직배비"
                value={saleMoveFee}
                onChange={e => setSaleMoveFee(formatNumber(e.target.value))}
              />
              <div />
              <InputBox label="합계" value={saleTotal} readOnly className="col-span-2" />
            </div>
          </div>

          {/* 매입정보 */}
          <div className="border border-gray-300 rounded p-3">
            <ModalHeader title="매입정보" />
            <div className="grid grid-cols-3 gap-3 mt-3" style={{ gridTemplateRows: "repeat(2, auto)" }}>
              {/* 첫 줄: 단가, 매입액, 부가세액 */}
              <InputBox
                label="단가"
                value={buyUnitPrice}
                onChange={e => setBuyUnitPrice(formatNumber(e.target.value))}
                style={{ gridRow: "1", gridColumn: "1" }}
              />
              <InputBox
                label="매입액"
                value={buyAmount}
                onChange={e => setBuyAmount(formatNumber(e.target.value))}
                style={{ gridRow: "1", gridColumn: "2" }}
              />
              <InputBox
                label="부가세액"
                value={buyVat}
                onChange={e => setBuyVat(formatNumber(e.target.value))}
                style={{ gridRow: "1", gridColumn: "3" }}
              />
              {/* 두 번째 줄: 다착비, 계근비, 합계 */}
              <InputBox
                label="다착비"
                value={buyMultiFee}
                onChange={e => setBuyMultiFee(formatNumber(e.target.value))}
                style={{ gridRow: "2", gridColumn: "1" }}
              />
              <InputBox
                label="계근비"
                value={buyWeightFee}
                onChange={e => setBuyWeightFee(formatNumber(e.target.value))}
                style={{ gridRow: "2", gridColumn: "2" }}
              />
              <InputBox
                label="합계"
                value={buyTotal}
                readOnly
                style={{ gridRow: "2", gridColumn: "3" }}
              />
            </div>
          </div>

          {/* 운송마진 */}
          <div className="border border-gray-300 rounded p-3">
            <ModalHeader title="운송마진" />
            <div className="flex gap-2 mt-3">
              <InputBox label="금액" value={transportProfitAmt} readOnly className="flex-1" />
              <InputBox
                label="비율"
                value={transportProfitRate}
                onChange={e => handleTransportProfitRateChange(e.target.value)}
                className="flex-1"
              />
            </div>
          </div>

          {/* 비고 */}
          <div className="border border-gray-300 rounded p-3 flex flex-col">
            <ModalHeader title="비고" />
            <textarea
              value={remark}
              onChange={e => setRemark(e.target.value)}
              className="flex-1 w-full min-h-[200px] border border-[#CCCCCC] rounded-md px-2 py-2 focus:outline-none focus:ring-2 focus:ring-[#55b938] focus:border-[#55b938] resize-none mt-3"
              placeholder="비고를 입력하세요"
            />
          </div>
        </div>

        {/* 하단 버튼 */}
        <div className="flex justify-end gap-2 pt-4 border-t border-gray-300">
          {mode !== "view" && (
            <>
              <Button variant="primary" onClick={handleSave} disabled={isPending}>
                {isPending ? "저장 중..." : "저장(S)"}
              </Button>
              <Button variant="white" onClick={handleContinuousSave} disabled={isPending}>
                {isPending ? "저장 중..." : "연속저장(A)"}
              </Button>
            </>
          )}
          <Button variant="white" onClick={onClose} disabled={isPending}>
            닫기(X)
          </Button>
        </div>
      </div>

      {/* 매출처 검색 모달 */}
      <CompanySearchModal
        isOpen={isCompanySearchOpen}
        onClose={() => setIsCompanySearchOpen(false)}
        onSelect={(company: StCompanyCode) => {
          if (company.stCompanyName) {
            setSaleCustomer(company.stCompanyName); // 화면에 이름 표시
          }
          if (company.stCompanyCode) {
            setSaleCustomerCode(String(company.stCompanyCode)); // DB 저장용 코드 (number -> string 변환)
          }
          setIsCompanySearchOpen(false);
        }}
      />

      {/* 품목 검색 모달 */}
      <ItemCodeSearchModal
        isOpen={isItemSearchOpen}
        onClose={() => setIsItemSearchOpen(false)}
        onSelect={(item: ItemCode) => {
          if (item.stItemName) {
            setItemName(item.stItemName); // 화면에 이름 표시
          }
          if (item.stItemCode) {
            setItemCode(item.stItemCode); // DB 저장용 코드
          }
          setIsItemSearchOpen(false);
        }}
      />

      {/* 차량번호 검색 모달 */}
      <CarNumberSearchModal
        isOpen={isCarNumberSearchOpen}
        onClose={() => setIsCarNumberSearchOpen(false)}
        companyGb={companyGb} // 회사 구분 전달
        onSelect={(vehicle: VehicleCode) => {
          if (vehicle.stVehicleNumber1) {
            setVehicleNumber(vehicle.stVehicleNumber1); // 화면에 차량번호 표시
          }
          if (vehicle.stVehicleCode) {
            setVehicleCode(vehicle.stVehicleCode); // DB 저장용 코드
          }
          setIsCarNumberSearchOpen(false);
        }}
      />

      {/* 기사명 검색 모달 */}
      <DriverSearchModal
        isOpen={isDriverSearchOpen}
        onClose={() => setIsDriverSearchOpen(false)}
        onSelect={(driver: DriverCode) => {
          if (driver.stDriverName) {
            setDriverName(driver.stDriverName); // 화면에 기사명 표시
          }
          if (driver.stDriverCode) {
            setDriverCode(driver.stDriverCode); // DB 저장용 코드
          }
          if (driver.stDriverMobileTel) {
            setPhoneNumber(driver.stDriverMobileTel);
          }
          setIsDriverSearchOpen(false);
        }}
      />
    </ModalLayout>
  );
}
